import type { GraphCanonicalEntity, GraphEntityType } from "@salesko/contracts";
import { ArrowRight, Database, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useI18n } from "../../i18n";
import { entityMeta } from "../entityMeta";
import { entityLabel } from "../i18n";
import { canonicalEntityRegistrySubtitle } from "../lib/canonicalRegistry";
import { compactDateTime } from "../lib/format";
import { DataTable } from "./data/DataTable";
import type { DataTableColumnDef } from "./data/tableTypes";
import { useColumnVisibility } from "./data/useColumnVisibility";

type CanonicalEntityRegistryPanelProps = {
  activeEntityId: string | null;
  count: number;
  enabled: boolean;
  entityType: GraphEntityType | undefined;
  error: string | undefined;
  items: GraphCanonicalEntity[];
  onSelectEntity: (entity: GraphCanonicalEntity) => void;
  pending: boolean;
  query: string;
};

type CanonicalRegistryTableRow = {
  confidencePercent: number;
  entity: GraphCanonicalEntity;
  lastSeenLabel: string;
  subtitle: string;
  typeLabel: string;
};

export function CanonicalEntityRegistryPanel({
  activeEntityId,
  count,
  enabled,
  entityType,
  error,
  items,
  onSelectEntity,
  pending,
  query
}: CanonicalEntityRegistryPanelProps) {
  const { t } = useI18n();
  const title = entityType ? entityLabel(t, entityType) : t.canonicalRegistry.allTitle;
  const trimmedQuery = query.trim();
  const [columnVisibility, setColumnVisibility] = useColumnVisibility(`canonical-registry:${entityType ?? "all"}`, {
    confidence: false
  });
  const canonicalRegistryColumnLabels = useMemo(
    () => ({
      actions: t.canonicalRegistry.columns.actions,
      confidence: t.canonicalRegistry.columns.confidence,
      frames: t.canonicalRegistry.columns.frames,
      identity: t.canonicalRegistry.columns.identity,
      lastSeen: t.canonicalRegistry.columns.lastSeen,
      nodes: t.canonicalRegistry.columns.nodes,
      type: t.canonicalRegistry.columns.type
    }),
    [t]
  );
  const rows = useMemo(
    () =>
      items.map((entity) => ({
        confidencePercent: Math.round(entity.confidence * 100),
        entity,
        lastSeenLabel: compactDateTime(entity.lastSeenAt),
        subtitle: canonicalEntityRegistrySubtitle(entity, t),
        typeLabel: entityLabel(t, entity.entityType)
      })),
    [items, t]
  );
  const columns = useMemo<Array<DataTableColumnDef<CanonicalRegistryTableRow>>>(
    () => [
      {
        accessorFn: (row) => row.entity.label,
        cell: ({ row }) => (
          <button
            aria-current={row.original.entity.id === activeEntityId ? "true" : undefined}
            className={
              row.original.entity.id === activeEntityId
                ? "canonical-registry-identity-button is-active"
                : "canonical-registry-identity-button"
            }
            onClick={() => onSelectEntity(row.original.entity)}
            type="button"
          >
            <CanonicalRegistryIcon entityType={row.original.entity.entityType} />
            <span className="data-table-main">
              <strong>{row.original.entity.label}</strong>
              <small>{row.original.subtitle}</small>
            </span>
          </button>
        ),
        enableHiding: false,
        header: t.canonicalRegistry.columns.identity,
        id: "identity"
      },
      {
        accessorFn: (row) => row.typeLabel,
        cell: ({ row }) => (
          <span className={`canonical-registry-type-badge canonical-registry-type-badge-${entityMeta[row.original.entity.entityType].tone}`}>
            {row.original.typeLabel}
          </span>
        ),
        header: t.canonicalRegistry.columns.type,
        id: "type"
      },
      {
        accessorFn: (row) => row.entity.nodeCount,
        cell: ({ row }) => row.original.entity.nodeCount,
        header: t.canonicalRegistry.columns.nodes,
        id: "nodes",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.entity.frameRecordCount,
        cell: ({ row }) => row.original.entity.frameRecordCount,
        header: t.canonicalRegistry.columns.frames,
        id: "frames",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.entity.lastSeenAt,
        cell: ({ row }) => row.original.lastSeenLabel,
        header: t.canonicalRegistry.columns.lastSeen,
        id: "lastSeen",
        sortDescFirst: true
      },
      {
        accessorFn: (row) => row.confidencePercent,
        cell: ({ row }) => `${row.original.confidencePercent}%`,
        header: t.canonicalRegistry.columns.confidence,
        id: "confidence",
        sortDescFirst: true
      },
      {
        cell: ({ row }) => (
          <button
            aria-label={t.canonicalRegistry.openIdentityAriaLabel(row.original.entity.label)}
            className="icon-button canonical-registry-action"
            onClick={() => onSelectEntity(row.original.entity)}
            title={t.canonicalRegistry.openIdentity}
            type="button"
          >
            <ArrowRight size={15} />
          </button>
        ),
        enableHiding: false,
        enableSorting: false,
        header: t.canonicalRegistry.columns.actions,
        id: "actions"
      }
    ],
    [activeEntityId, onSelectEntity, t]
  );

  return (
    <div className="canonical-registry-panel">
      <header className="canonical-registry-header">
        <span className="canonical-registry-mark" aria-hidden="true">
          {pending ? <Loader2 className="spin" size={22} /> : <Database size={22} />}
        </span>
        <div className="canonical-registry-title">
          <p className="eyebrow">{t.canonicalRegistry.eyebrow}</p>
          <h2>{title}</h2>
          <span>
            {enabled
              ? t.canonicalRegistry.count(count)
              : t.canonicalRegistry.signedInRegistry}
          </span>
        </div>
      </header>

      {!enabled ? (
        <p className="canonical-registry-state">
          {t.canonicalRegistry.signIn}
        </p>
      ) : error ? (
        <p className="canonical-registry-state" role="alert">
          {t.canonicalRegistry.unavailable}
        </p>
      ) : pending ? (
        <p className="canonical-registry-state">{t.canonicalRegistry.loading}</p>
      ) : items.length === 0 ? (
        <p className="canonical-registry-state">
          {trimmedQuery ? t.canonicalRegistry.noSearchMatch : t.canonicalRegistry.empty}
        </p>
      ) : (
        <DataTable
          className="canonical-registry-table"
          columnLabels={canonicalRegistryColumnLabels}
          columnVisibility={columnVisibility}
          columns={columns}
          data={rows}
          emptyLabel={trimmedQuery ? t.canonicalRegistry.noSearchMatch : t.canonicalRegistry.empty}
          getRowId={(row) => row.entity.id}
          initialSorting={[{ desc: true, id: "lastSeen" }]}
          labels={t.dataTable}
          onColumnVisibilityChange={setColumnVisibility}
        />
      )}
    </div>
  );
}

function CanonicalRegistryIcon({ entityType }: { entityType: GraphEntityType }) {
  const meta = entityMeta[entityType];
  const Icon = meta.icon;
  return (
    <span className={`canonical-registry-icon canonical-registry-icon-${meta.tone}`} aria-hidden="true">
      <Icon size={18} />
    </span>
  );
}
